import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TwoColumnPageLayoutComponent } from '@lb/components/page-templates/two-column-page-layout/two-column-page-layout.component';
import { EnrollmentStateService } from '@lb/state/enrollment-state.service';
import { MDAlertService, MDContentBoxComponent, MDHeaderComponent, MDIconComponent, MDToolTipDirective } from '@melodic-ui/components';
import { combineLatest, filter, take } from 'rxjs';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import { SchedulerProgressComponent } from '../../../shared/components/scheduler-progress/scheduler-progress.component';
import { format } from 'date-fns';
import { SchedulerValidationUtils } from '@lb/components';
import { GlobalTimezoneService } from '@lb/services/global-timezone.service';
import { SchedulerFormService } from '@lb/schedule/services/scheduler-form.service';
import { SchedulerStateService } from '@lb/schedule/services/scheduler-state.service';
import { LearnerUserStateService } from '../../../shared/state/learner-users-state.service';

@Component({
	selector: 'lrnr-schedule',
	standalone: true,
	imports: [
		CommonModule,
		TwoColumnPageLayoutComponent,
		MDContentBoxComponent,
		MDHeaderComponent,
		MDIconComponent,
		MDToolTipDirective,
		LoaderComponent,
		SchedulerProgressComponent
	],
	templateUrl: './schedule.component.html',
	styleUrls: ['./schedule.component.scss']
})
export class ScheduleComponent {
	private readonly _activatedRoute = inject(ActivatedRoute);
	private readonly _enrollmentStateService = inject(EnrollmentStateService);
	private readonly _learnerUserStateService = inject(LearnerUserStateService);
	private readonly _schedulerStateService = inject(SchedulerStateService);
	private readonly _schedulerFormService = inject(SchedulerFormService);
	private readonly _globalTimezoneService = inject(GlobalTimezoneService);
	private readonly _router = inject(Router);
	private readonly _alertService = inject(MDAlertService);

	private readonly _activeLearnerUserID = this._learnerUserStateService.activeLearnerUserID;

	enrollmentID = signal<number | null>(null);
	submitting = signal(false);
	currentEnrollment = this._enrollmentStateService.currentEnrollment;
	isEnrollmentsLoading = this._enrollmentStateService.loading;
	currentEnrollmentHistory = computed(() => this.currentEnrollment()?.currentEnrollmentHistory);

	currentStepIndex = this._schedulerStateService.currentStepIndex;
	currentSchedulerStep = this._schedulerStateService.currentSchedulerStep;
	isFirstStep = this._schedulerStateService.isFirstStep;
	isLastStep = this._schedulerStateService.isLastStep;
	canProceedToNextStep = this._schedulerStateService.canProceedToNextStep;
	canFinish = this._schedulerStateService.canFinish;
	nextButtonTooltip = this._schedulerStateService.nextButtonTooltip;
	finishButtonTooltip = this._schedulerStateService.finishButtonTooltip;

	constructor() {
		this._schedulerFormService.initializeWizardForm();
		this.enrollmentID.set(Number(this._activatedRoute.snapshot.params['id']));

		combineLatest([toObservable(this.enrollmentID), toObservable(this._activeLearnerUserID)])
			.pipe(
				takeUntilDestroyed(),
				filter(([enrollmentID, activeLearnerUserID]) => enrollmentID !== null && activeLearnerUserID !== undefined)
			)
			.subscribe(([enrollmentID, activeLearnerUserID]) => {
				activeLearnerUserID && enrollmentID && this._enrollmentStateService.loadEnrollments(activeLearnerUserID, 'learner', enrollmentID);
			});

		toObservable(this._enrollmentStateService.currentEnrollment)
			.pipe(
				takeUntilDestroyed(),
				filter((enrollment) => Boolean(enrollment))
			)
			.subscribe((enrollment) => {
				if (enrollment === undefined) return;

				const history = enrollment.currentEnrollmentHistory;

				if (history) {
					// Only carry over the enrollment's saved start date if it still satisfies the
					// "at least 7 days out" rule; otherwise default to the minimum allowed start date.
					const historyDatePart = history.startDate ? history.startDate.toString().split('T')[0] : null;
					const historyStartDate = historyDatePart ? new Date(historyDatePart.replace(/-/g, '/')) : null;
					const startDateToUse =
						historyStartDate && !SchedulerValidationUtils.isDateTooEarly(historyStartDate)
							? historyStartDate
							: SchedulerValidationUtils.getMinimumStartDate();
					const formattedStartDate = format(startDateToUse, 'yyyy-MM-dd');

					// Get current timezone from global timezone selector as fallback
					const globalTimezone = this._globalTimezoneService.effectiveTimezone();
					this._schedulerStateService.initializeState(false, enrollment);

					// Populate form with enrollment history data
					// Use history timezone if available, otherwise use the global timezone selector value
					this._schedulerFormService.populateFromData({
						configuration: {
							frequency: history.lessonFrequency,
							startDate: formattedStartDate,
							timeZone: enrollment.learnerTimeZoneID ?? globalTimezone?.timeZoneID
						}
					});
				}
			});
	}

	goToPreviousStep(): void {
		this._schedulerStateService.goToPreviousStep();
	}

	goToNextStep(): void {
		this._schedulerStateService.goToNextStep();
	}

	completeSchedulerStep(): void {
		const enrollmentID = this.enrollmentID();
		if (!enrollmentID || this.submitting()) {
			return;
		}

		// Submitting builds the enrollment's whole lesson schedule server-side, so the wait is long
		// enough that an unguarded button reads as unresponsive and invites a second, duplicate submit.
		this.submitting.set(true);

		this._schedulerStateService
			.completeSchedulerStep(enrollmentID, this.currentEnrollment()?.notes || [])
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.submitting.set(false);

					this._alertService.addAlert({
						title: 'Successfully saved your schedule.',
						type: 'success',
						message: 'Saved your schedule successfully, we will match you with an instructor shortly.',
						duration: 5000
					});

					this._schedulerFormService.resetWizard();
					this._schedulerStateService.resetSchedulerState();
					this._router.navigate(['/enrollments']);
				},
				error: (error) => {
					this.submitting.set(false);

					this._alertService.addAlert({
						title: 'Failed to save your schedule.',
						type: 'error',
						message: 'Please try again.',
						duration: 5000
					});

					console.error('Error saving enrollment schedule', error);
				}
			});
	}
}
